import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  WritableSignal,
  booleanAttribute,
  effect,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FilterOperator, FilterValueInterface, ScoringDefinitionInterface, ScoringRuleInterface } from '@vendasta/crm';
import { distinctUntilChanged } from 'rxjs';
import { DropdownComponent, ObjectType } from '@galaxy/crm/static';
import { FilterInputType } from '../../interfaces';

import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DropdownHeaderDirective } from '@galaxy/crm/static';
import { DropdownContentDirective } from '@galaxy/crm/static';
import { ScoringDefinitionCustomFilterOutputComponent } from '../fields/custom-filter-output.component';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ScoringDefinitionFieldIdComponent } from '../fields/field-id.component';
import { ScoringDefinitionFieldOperatorComponent } from '../fields/operator.component';
import { ScoringDefinitionFieldValueComponent } from '../fields/value.component';
import { TranslateModule } from '@ngx-translate/core';
import { FilterOverridePipe } from '../pipes/filter-override.pipe';
import { FieldNamePipe } from '../pipes/field-name.pipe';
import { OperatorNamePipe } from '../pipes/operator-name.pipe';
import { FieldValuePipe } from '../pipes/field-value.pipe';
import { OperatorValueIsRequiredPipe } from '../pipes/operator-value-is-required.pipe';
import { FirstRuleFilterPipe } from '../pipes/first-rule-filter.pipe';
import { PointsPipe } from '../pipes/points.pipe';
import { SignPipe } from '../pipes/sign.pipe';

@Component({
  selector: 'crm-scoring-definition-card',
  templateUrl: './card.component.html',
  styleUrl: './card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    NgTemplateOutlet,
    DropdownHeaderDirective,
    DropdownContentDirective,
    ScoringDefinitionCustomFilterOutputComponent,
    GalaxyFormFieldModule,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatIcon,
    ScoringDefinitionFieldIdComponent,
    ScoringDefinitionFieldOperatorComponent,
    ScoringDefinitionFieldValueComponent,
    MatButton,
    AsyncPipe,
    TranslateModule,
    FilterOverridePipe,
    FieldNamePipe,
    OperatorNamePipe,
    FieldValuePipe,
    OperatorValueIsRequiredPipe,
    FirstRuleFilterPipe,
    PointsPipe,
    SignPipe,
  ],
})
export class ScoringDefinitionCardComponent implements AfterViewInit {
  private readonly _scoringDefinition = signal<ScoringDefinitionInterface | undefined>(undefined);
  @Input({ required: true })
  set scoringDefinition(value: ScoringDefinitionInterface) {
    this._scoringDefinition.set(value);
  }
  get scoringDefinition(): WritableSignal<ScoringDefinitionInterface | undefined> {
    return this._scoringDefinition;
  }
  @Output() scoringDefinitionChange = new EventEmitter<ScoringDefinitionInterface>();

  @Input({ transform: booleanAttribute }) disabled = false;

  @Input({ required: true }) objectType?: ObjectType;

  private _modified = false;
  @Output() modified = new EventEmitter<void>();

  protected readonly contributionCtrl = new FormControl<number>(0, {
    nonNullable: true,
    validators: [this.validateContribution.bind(this)],
  });
  protected readonly actions = [{ label: 'ACTIONS.DELETE', callback: () => this.handleDeleteClick() }];
  protected readonly FilterInputType = FilterInputType;
  protected readonly initialScoringDefinition = signal<ScoringDefinitionInterface | undefined>(undefined);

  @ViewChild(DropdownComponent) panel?: DropdownComponent;

  constructor() {
    this.contributionCtrl.valueChanges
      .pipe(takeUntilDestroyed(), distinctUntilChanged())
      .subscribe((v) => this.updateContributionValue(v));

    effect(() => {
      const scoringDefinition = this._scoringDefinition();
      if (scoringDefinition) {
        this.contributionCtrl.setValue(this.getAbsoluteValue(scoringDefinition.contribution ?? 0));
      }
    });
    effect(() => {
      const initialScoringDefinition = this.initialScoringDefinition();
      const scoringDefinition = this._scoringDefinition();
      // only checks for modifications if the initial state is set
      if (initialScoringDefinition && !this._modified) {
        this._modified = !this.areScoringDefinitionsEqual(initialScoringDefinition, scoringDefinition ?? {});
        if (this._modified) {
          this.modified.emit();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // resets to the initial state to create a copy of the current scoring definition for comparison
    this.reset();
    const scoringDefinition = this._scoringDefinition();
    if (!scoringDefinition?.scoringDefinitionId) {
      this.panel?.open();
    }
  }

  reset(): void {
    const scoringDefinition = this._scoringDefinition();
    this.initialScoringDefinition.set(scoringDefinition ? JSON.parse(JSON.stringify(scoringDefinition)) : undefined);
    this._modified = false;
  }

  async handleAddFilterClick(): Promise<void> {
    const emptyRule: ScoringRuleInterface = {
      crmObjectType: this.objectType,
      objectRules: [{ filter: { fieldId: '', values: [] } }],
    };
    this._scoringDefinition.update((v) => {
      if (!v) {
        return v;
      }
      const rules = v.scoringRules ?? [];
      return { ...v, scoringRules: rules.concat(emptyRule) };
    });
    this.scoringDefinitionChange.emit(this._scoringDefinition());
  }

  async handleDeleteClick(): Promise<void> {
    this._scoringDefinition.set(undefined);
    this.scoringDefinitionChange.emit(this._scoringDefinition());
  }

  async handleRemoveRule(index: number): Promise<void> {
    this._scoringDefinition.update((v) => {
      const rules = v?.scoringRules ?? [];
      if (rules[index]) {
        rules.splice(index, 1);
        return { ...v, scoringRules: rules };
      }
      return v;
    });
    this.scoringDefinitionChange.emit(this._scoringDefinition());
  }

  protected setRuleFieldId(index: number, rule: ScoringRuleInterface, fieldId?: string): void {
    if (rule?.objectRules?.[0]?.filter) {
      rule.objectRules[0].filter.fieldId = fieldId;
      this.updateRuleValue(index, rule);
    }
  }

  protected setRuleOperator(index: number, rule: ScoringRuleInterface, operator?: FilterOperator): void {
    if (rule?.objectRules?.[0]?.filter) {
      rule.objectRules[0].filter.operator = operator;
      this.updateRuleValue(index, rule);
    }
  }

  protected setRuleValue(index: number, rule: ScoringRuleInterface, value: FilterValueInterface[]): void {
    if (rule?.objectRules?.[0]?.filter) {
      if (JSON.stringify(rule.objectRules[0].filter.values) !== JSON.stringify(value)) {
        rule.objectRules[0].filter.values = value;
        this.updateRuleValue(index, rule);
      }
    }
  }

  private updateContributionValue(value: number): void {
    this._scoringDefinition.update((v) => {
      if (!v) {
        return v;
      }
      if (v.contribution === value) {
        return v;
      }
      return { ...v, contribution: value };
    });
    this.scoringDefinitionChange.emit(this.prepareScoringDefinition(this._scoringDefinition() ?? {}));
  }

  private updateRuleValue(index: number, value: ScoringRuleInterface): void {
    this._scoringDefinition.update((v) => {
      if (!v) {
        return v;
      }
      if (!v.scoringRules?.[index]) {
        return v;
      }
      v.scoringRules[index] = value;
      return { ...v, scoringRules: v.scoringRules };
    });
    this.scoringDefinitionChange.emit(this._scoringDefinition());
  }

  private validateContribution(control: AbstractControl): ValidationErrors | null {
    if (control.value < 1) {
      return { outOfRange: true };
    }
    return null;
  }

  private prepareScoringDefinition(value: ScoringDefinitionInterface): ScoringDefinitionInterface {
    value.contribution = this.getSignedValue(value.contribution ?? 0);
    return value;
  }

  private getAbsoluteValue(value: number): number {
    return Math.abs(value);
  }

  private getSignedValue(value: number): number {
    const contribution = this.initialScoringDefinition()?.contribution ?? 0;
    if (contribution < 0) {
      return Math.abs(value) * -1;
    }
    return value;
  }

  private areScoringDefinitionsEqual(a: ScoringDefinitionInterface, b: ScoringDefinitionInterface): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }
}
